import { useEffect, useState } from 'react';
import {
  Descriptions,
  notification,
  Form,
  Space,
  Row,
  Col,
  Input,
  Typography,
  Card,
  Skeleton,
  Popconfirm,
  Alert,
  Table,
  Modal,
} from 'antd';

import MESSAGES from '../../../enums/Messages';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import CustomButton from '../../../components/CustomButton';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import { useParams, Link, useHistory } from 'react-router-dom';
import {
  info as getInfo,
  filter as filterAjaxTable,
  patchModule,
  post,
  put,
  remove,
} from '../../../api/module/request_mod_payments';
import { catchException } from '../../../utils/helpers/helpers';
import '../Form/style.scss';
import AjaxTable from '../../../components/AjaxTable';
import ModificationModuleSaveButton from '../../../components/ModificationComponents';
import { CustomTooltip } from '../../../components/CustomTooltip';
import EllipsisModal from '../../../components/EllipsisModal';
import ModificationPaymentForm from './form';

const { Title } = Typography;

const ModificationPayments = ({ sendPerm }) => {
  var { modification_id } = useParams();
  const navigate = useHistory();

  const [dataInfo, setDataInfo] = useState();
  const [reloadSource, setReloadSource] = useState();
  const [expandedKeys, setExpandedKeys] = useState([]);

  const [initialValues, setInitialValues] = useState();
  const [moduleForm] = Form.useForm();

  const [loadingSend, setLoadingSend] = useState(false);

  const [toDelete, setToDelete] = useState();

  const [valueData, setValueData] = useState({});

  useEffect(() => {
    async function fetchData() {
      try {
        const { data } = await getInfo(modification_id);
        setDataInfo(data);
        const compareTotal = data.valor_total_origen
          ? data.valor_total_modificado
          : data.valor_contrato;
        setValueData({
          total: compareTotal,
          currentTotalText: formatCurrency(data.valor_contrato),
          totalModifiedText:
            data.valor_total_origen &&
            formatCurrency(data.valor_total_modificado),
          used: data.valor_asignado,
          usedText: formatCurrency(data.valor_asignado),
          pending: compareTotal - data.valor_asignado,
          pendingText: formatCurrency(compareTotal - data.valor_asignado),
        });
      } catch (err) {
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description: MESSAGES.DESC_ERROR_GET_MSG,
          duration: 2,
        });
      }
    }
    fetchData();
  }, [modification_id]);

  const handleEdit = (record) => {
    setInitialValues(record);
  };

  const changeValueData = (va) => {
    setValueData((s) => {
      const newPending = s.total - va;
      return {
        ...s,
        used: va,
        usedText: formatCurrency(va),
        pending: newPending,
        pendingText: formatCurrency(newPending),
      };
    });
  };

  const deleteRecord = async (record) => {
    try {
      setToDelete(record.id);
      const res = await remove(record.id);
      changeValueData(res.data.valor_asignado);
      setDataInfo((s) => ({ ...s, max_num_pago: res.data.max_num_pago }));
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_DELETE_MSG,
        duration: 2,
      });
      setReloadSource({});
      if (initialValues?.id === record.id) {
        setInitialValues();
      }
    } catch (error) {
      console.log(error);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_DELETE_MSG,
        duration: 2,
      });
    } finally {
      setToDelete();
    }
  };

  const columns = [
    {
      title: 'Pago No.',
      dataIndex: 'numero',
      key: 'numero',
    },
    {
      title: 'Valor del pago VIGENTE',
      dataIndex: 'valor_vigente',
      render: (text) =>
        text
          ? `$ ${text
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
          : null,
    },
    {
      title: 'Valor del pago MODIFICADO',
      dataIndex: 'valor',
      render: (text) => {
        return `$ ${text
          .toFixed(2)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
      },
    },
    {
      title: 'Modificaciones',
      dataIndex: 'modificaciones',
      render: (t, record) =>
        record.origen !== 3 && t > 1 ? 'Modificado' : null,
    },
    {
      title: 'Estado de aprobación',
      dataIndex: 'estado_aprobacion',
    },
    {
      title: 'Acciones',
      key: 'Acciones',
      align: 'center',
      width: 200,
      render: (_, record) =>
        record.estado_aprobacion === 'Sin gestionar' || record.origen === 3 ? (
          <Space direction={'vertical'} style={{ width: '100%' }}>
            <CustomButton
              block
              htmlType={'button'}
              text="Editar"
              onClick={() => handleEdit(record)}
            />
            <Popconfirm
              title={MESSAGES.DELETE_CONFIRM_MSG}
              onConfirm={() => deleteRecord(record)}
            >
              <CustomButton
                block
                type={'default'}
                htmlType={'button'}
                danger
                text="Eliminar"
                loading={toDelete === record.id}
              />
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  const breadcrumbItems = [
    {
      text: 'Módulo Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}`,
    },
    {
      text: 'Formulario de Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo?.proyecto_id}/${modification_id}`,
    },
    {
      text: 'Forma de pago',
    },
  ];

  const expandedRowRender = (record) => {
    const columns = [
      {
        title: 'Producto asociado',
        dataIndex: 'producto_nombre',
        width: '20rem',
        render: (text, record) => (
          <EllipsisModal
            text={`${record.codigo}. ${text}`}
            title="Producto asociado"
          />
        ),
      },
      {
        title: (
          <>
            Tipo{' '}
            <CustomTooltip
              title={
                <>
                  <ul>
                    <li>PF: Producto Fijo</li>
                    <li>GF: Gasto Fijo</li>
                    <li>PV: Producto Variable</li>
                    <li>GV: Gasto Variable</li>
                    <li>PVsT: Producto Variable sin Tope</li>
                    <li>GVsT: Gasto Variable sin Tope</li>
                  </ul>
                </>
              }
            />
          </>
        ),
        dataIndex: 'tipo',
        width: '5rem',
      },
      {
        title: 'Modificaciones',
        dataIndex: 'origen',
        render: (_, record) =>
          record.requiere_eliminacion
            ? 'Eliminado'
            : record.origen === 1
            ? ''
            : record.origen === 2
            ? 'Modificada'
            : 'Nueva',
        align: 'center',
        width: '8rem',
      },
      {
        title: 'Valor a pagar por producto VIGENTE',
        dataIndex: 'valor_vigente',
        width: '10rem',
        render: (t) =>
          t ? `$ ${t.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : null,
      },
      {
        title: 'Valor a pagar por producto MODIFICADO',
        dataIndex: 'valor',
        width: '10rem',
        render: (t, record) =>
          !record.requiere_eliminacion && t
            ? `$ ${t.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
            : null,
      },
    ];
    return (
      <Table
        rowKey="id"
        columns={columns}
        style={{ paddingRight: '1rem' }}
        dataSource={record.ctm_productos}
        pagination={false}
      />
    );
  };

  const handleFinishModule = async (values) => {
    setLoadingSend(true);
    try {
      await patchModule(dataInfo.id, values);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: 'Módulo actualizado correctamente.',
        duration: 2,
      });
      setTimeout(() => {
        navigate.push(
          `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`,
        );
      }, MESSAGES.TIME);
    } catch (error) {
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_POST_MSG,
        duration: 2,
      });
    } finally {
      setLoadingSend(false);
    }
  };

  const handleFinishPayment = async (values) => {
    try {
      let res;
      if (initialValues?.id) {
        res = await put(initialValues.id, {
          ...values,
          modulo_modificacion: dataInfo.id,
        });
        changeValueData(res.data.valor_asignado);
      } else {
        res = await post({ ...values, modulo_modificacion: dataInfo.id });
        changeValueData(res.data.valor_asignado);
      }
      setDataInfo((s) => ({ ...s, max_num_pago: res.data.max_num_pago }));
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setReloadSource({});
      setInitialValues();
    } catch (error) {
      catchException(error, MESSAGES.DESC_ERROR_POST_MSG);
      throw error;
    }
  };

  function formatCurrency(number) {
    number = number ? number : 0;
    const formatter = new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return formatter
      .format(number)
      .replace(/\./g, 'x')
      .replace(/,/g, '.')
      .replace(/x/g, ',');
  }

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <div className="box-title">
        <Title className={'text-uppercase'} level={2}>
          MODIFICACIÓN CONTRACTUAL PRESUPUESTAL
        </Title>
      </div>
      {dataInfo ? (
        <>
          <Modal
            destroyOnClose
            open={!!initialValues}
            footer={null}
            closable
            onCancel={() => setInitialValues()}
            title="Formulario de pago"
            width={'90%'}
          >
            <ModificationPaymentForm
              initialValues={initialValues}
              mod_module_c_id={dataInfo.modulo_c}
              currencyText={dataInfo.moneda_descripcion}
              onSave={handleFinishPayment}
            />
          </Modal>
          <Space direction={'vertical'} style={{ width: '100%' }}>
            <Descriptions
              layout="horizontal"
              column={2}
              bordered
              labelStyle={{ maxWidth: '5rem' }}
              className="fade-in"
            >
              <Descriptions.Item label="Contratista / Proveedor" span={2}>
                <Row align="middle">
                  <Col flex="auto">{dataInfo.contratista_nombre}</Col>
                  <Col flex="0 0">
                    <CustomButton
                      htmlType="button"
                      text={
                        <Link
                          to={`${INTERNAL_LINKS.RESUME_CONTRACT_SEARCH}-detail/${dataInfo.contrato_id}/${dataInfo.proyecto_id}/${dataInfo.cargo_id}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver históricos contractuales
                        </Link>
                      }
                    />
                  </Col>
                </Row>
              </Descriptions.Item>
              <Descriptions.Item label="Cargo/Rol" span={2}>
                {dataInfo.cargo_nombre}
              </Descriptions.Item>
            </Descriptions>
          </Space>
          <Form
            layout="vertical"
            autoComplete="off"
            form={moduleForm}
            initialValues={dataInfo}
            onFinish={handleFinishModule}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          >
            <Space direction={'vertical'} style={{ width: '100%' }}>
              <Card
                size={'small'}
                bordered={false}
                title={<Title level={3}>5. Cambio forma de pago</Title>}
                style={{ padding: 0 }}
                headStyle={{ padding: 0 }}
                bodyStyle={{ padding: 0 }}
              >
                {sendPerm && (
                  <Form.Item
                    label="Por favor, digite en letras el número de la cláusula del contrato a modificar"
                    name="clausula"
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el nombre de una cláusula',
                      },
                      {
                        max: 100,
                        message:
                          'El campo debe tener un máximo de 100 caracteres',
                      },
                    ]}
                  >
                    <Input allowClear />
                  </Form.Item>
                )}
              </Card>
            </Space>
            <Alert
              type={valueData.pending !== 0 ? 'error' : 'info'}
              message="Información:"
              description={
                <ul>
                  <li>
                    El valor total del contrato incluyendo posibles
                    modificaciones vigentes:{' '}
                    <span style={{ fontStyle: 'bold' }}>
                      {valueData.currentTotalText}
                    </span>
                  </li>
                  {valueData.totalModifiedText && (
                    <li>
                      Valor total contrato modificado (incluyendo impuestos,
                      tasas y contribuciones):{' '}
                      <span style={{ fontStyle: 'bold' }}>
                        {valueData.totalModifiedText}
                      </span>
                    </li>
                  )}
                  <li>
                    Ha asignado:{' '}
                    <span style={{ fontStyle: 'bold' }}>
                      {valueData.usedText}
                    </span>
                  </li>
                  <li>
                    Valor Pendiente por asignar:{' '}
                    <span style={{ fontStyle: 'bold' }}>
                      {valueData.pendingText}
                    </span>
                  </li>
                </ul>
              }
              style={{ marginBottom: '20px' }}
            />
            <div
              style={{
                justifyContent: 'start',
                display: 'flex',
                marginBottom: '1rem',
              }}
            >
              <CustomButton
                htmlType={'button'}
                text="Agregar nuevo pago"
                onClick={() =>
                  setInitialValues({ numero: dataInfo.max_num_pago + 1 })
                }
              />
            </div>
            <AjaxTable
              columns={columns}
              searchInput
              tableTitle={'Listado de pagos'}
              expandedRowRender={{
                expandedRowRender,
                // expandRowByClick: true,
                expandedRowKeys: expandedKeys,
                onExpandedRowsChange: (v) => setExpandedKeys(v),
              }}
              onSearchExpand
              onExpandAll={(ds) => setExpandedKeys(ds.map((v) => v.id))}
              endpoint={filterAjaxTable}
              reloadSource={reloadSource}
              query={dataInfo.id}
              exportCSV
            />

            <Form.Item
              label="Justificación"
              name="justificacion"
              rules={[
                {
                  required: true,
                  message: 'Ingresa la Justificación',
                },
              ]}
            >
              <Input.TextArea allowClear />
            </Form.Item>

            <Space>
              <Popconfirm
                placement="topLeft"
                title="Si realizó cambios por favor guarde"
                cancelText="No"
                okText={
                  <Link
                    to={`${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`}
                  >
                    Sí
                  </Link>
                }
              >
                <CustomButton
                  htmlType="button"
                  className={'back-button'}
                  type="default"
                  text="Volver"
                />
              </Popconfirm>
              <ModificationModuleSaveButton
                onConfirm={() => moduleForm.submit()}
                loading={loadingSend}
              />
            </Space>
          </Form>
        </>
      ) : (
        <Skeleton active loading />
      )}
    </>
  );
};

export default ModificationPayments;
